import React, { useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useRowHighlight } from '../utils/useRowHighlight';
import { useAppShell } from '../contexts/AppShellContext';
import { useProxyData } from '../contexts/ProxyDataContext';
import { useProxyWorkflowContext } from '../hooks/useProxyWorkflowContext';
import { PROXY_SEED_TODAY, proxyNowStamp } from '../data/mockProxyJobs';
import { buildProxyIntakeTasks, buildMaterialFollowupTask, materialFollowupTaskFor } from '../data/proxyWorkflowRegistry';
import { buildShortageTask } from '../data/workflows/shortageFollowupWorkflow';
import {
  buildFollowUpTracker, OUTREACH_KIND_LABEL, FOLLOW_UP_INTERVAL_DAYS, CONTEST_INTERVAL_DAYS,
  type TrackerRow, type TrackerStatus,
} from '../utils/proxyFollowUpTracker';

// The Follow Ups queue: every chase that's out with an external party and
// still open, with when it went out and when the next one is due. Teams
// don't chase daily; the next action date tells them when to come back.
// A reply, a bounce or a Discovery hold shows on the chase it belongs to.
const STATUS_CLASS: Record<TrackerStatus, string> = {
  Overdue: 'ra-abort', Due: 'ra-cancel', Waiting: 'ra-noaction', Replied: 'ra-followup', Bounced: 'ra-abort', 'On hold': 'ra-hold',
};

function fmtDate(iso: string): string {
  return new Date(`${iso}T00:00:00`).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function nextLabel(row: TrackerRow): string {
  if (row.status === 'Due') return 'Today';
  const days = Math.round((Date.parse(row.nextAction) - Date.parse(PROXY_SEED_TODAY)) / 86_400_000);
  return days < 0 ? `${-days}d overdue` : `in ${days}d`;
}

export function ProxyFollowUpsPage() {
  const { setTopbar } = useAppShell();
  const navigate = useNavigate();
  const { updateJob } = useProxyData();
  const ctx = useProxyWorkflowContext();
  const { rowRef, highlightClass } = useRowHighlight('row');

  useEffect(() => {
    setTopbar('Proxy Follow Ups', `Every chase that's out and waiting on an answer. The next one is due ${FOLLOW_UP_INTERVAL_DAYS} days after the last (${CONTEST_INTERVAL_DAYS} for contest forms). Replies and bounces show on the chase they answer.`);
  }, [setTopbar]);

  const rows = useMemo(() => buildFollowUpTracker(ctx.jobs, ctx.receipts, PROXY_SEED_TODAY, ctx.emails), [ctx.jobs, ctx.receipts, ctx.emails]);
  const count = (status: TrackerStatus) => rows.filter(r => r.status === status).length;
  const tasks = useMemo(() => buildProxyIntakeTasks(ctx), [ctx]);

  // The task Ace made from the reply or bounce on this chase.
  function responseTask(row: TrackerRow) {
    return tasks.find(t => t.sourceEmailId === row.responseEmailId && t.jobId === row.job.jobId);
  }

  function followUp(row: TrackerRow) {
    const { job } = row;
    const stamp = proxyNowStamp();
    if (row.kind === 'material') {
      const existing = materialFollowupTaskFor(tasks, job.jobId);
      if (existing) { navigate('/ace', { state: { proxyTask: existing } }); return; }
      updateJob(job.jobId, { materialFollowupRequestedAt: stamp });
      navigate('/ace', { state: { proxyTask: buildMaterialFollowupTask(job, stamp) } });
    } else if (row.kind === 'shortage') {
      updateJob(job.jobId, { shortageFollowupRequestedAt: stamp });
      navigate('/ace', { state: { proxyTask: buildShortageTask({ ...job, shortageFollowupRequestedAt: stamp }) } });
    } else {
      const task = buildProxyIntakeTasks(ctx).find(t => t.workflowType === 'PROXY_CONTEST' && t.jobId === job.jobId);
      if (task) navigate('/ace', { state: { proxyTask: task } });
    }
  }

  return (
    <div className="h-full overflow-auto px-6 py-6">
      <div data-tour="followups-counts" className="flex items-center gap-2 mb-4">
        <span className="tp ra-abort">{count('Overdue')} overdue</span>
        <span className="tp ra-cancel">{count('Due')} due today</span>
        <span className="tp ra-followup">{count('Replied')} replied</span>
        <span className="tp ra-abort">{count('Bounced')} bounced</span>
        <span className="tp ra-noaction">{count('Waiting')} waiting</span>
        <span className="tp ra-hold">{count('On hold')} on hold</span>
      </div>

      {rows.length === 0 ? (
        <div className="px-4 py-10 rounded-xl border border-trim bg-wash text-center">
          <div className="text-sm font-medium text-[#0F6E56] mb-0.5">Nothing out for follow-up</div>
          <div className="text-xs text-muted">Chases appear here once they're sent, with the date the next one is due.</div>
        </div>
      ) : (
        <table className="w-full text-sm border-collapse">
          <thead>
            <tr className="text-left text-xs text-muted">
              <th className="px-2 py-2 font-medium">Issuer / Job</th>
              <th className="px-2 py-2 font-medium">Chase</th>
              <th className="px-2 py-2 font-medium">Last sent</th>
              <th className="px-2 py-2 font-medium">Days elapsed</th>
              <th className="px-2 py-2 font-medium">Next follow-up</th>
              <th className="px-2 py-2 font-medium">Status</th>
              <th className="px-2 py-2" />
            </tr>
          </thead>
          <tbody>
            {rows.map(row => (
              <tr key={row.id} ref={rowRef(row.id)} data-tour={`followup-row-${row.id}`} className={`border-b border-trim ${highlightClass(row.id)}`}>
                <td className="px-2 py-2.5">
                  <div className="text-sm font-medium text-ink">{row.job.issuer}</div>
                  <div className="text-xs text-muted">Job {row.job.jobId}</div>
                </td>
                <td className="px-2 py-2.5 text-xs text-copy">
                  <div>{OUTREACH_KIND_LABEL[row.kind]}</div>
                  <div className="text-muted">{row.attempts} sent</div>
                </td>
                <td className="px-2 py-2.5 text-xs text-copy">
                  <div className="whitespace-nowrap">{fmtDate(row.lastSent)}</div>
                  <div className="text-muted truncate max-w-[220px]" title={row.lastRecipient}>to {row.lastRecipient}</div>
                </td>
                <td className="px-2 py-2.5 text-xs text-copy whitespace-nowrap">{row.daysElapsed}d</td>
                <td className="px-2 py-2.5 text-xs text-copy whitespace-nowrap">
                  <div>{fmtDate(row.nextAction)}</div>
                  <div className="text-muted">{nextLabel(row)}</div>
                </td>
                <td className="px-2 py-2.5">
                  <span className={`tp ${STATUS_CLASS[row.status]}`}>{row.status}</span>
                  {row.note && <div className="text-xs text-muted mt-1 max-w-[220px]">{row.note}</div>}
                </td>
                <td className="px-2 py-2.5 text-right">
                  {row.status === 'On hold' ? null : responseTask(row) ? (
                    <button onClick={() => navigate('/ace', { state: { proxyTask: responseTask(row) } })} className="text-xs font-medium text-[#2563EB] whitespace-nowrap">
                      {row.status === 'Bounced' ? 'Open exception' : 'Open reply'}
                    </button>
                  ) : (
                    <button onClick={() => followUp(row)} className="text-xs font-medium text-[#2563EB] whitespace-nowrap">
                      {row.status === 'Waiting' ? 'Follow up early' : 'Follow up'}
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
